import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useSSEGenerate } from '@/hooks/useSSEGenerate'
import { useToast } from '@/components/ToastContainer'
import { formatDate } from '@/ui/format'
import { cn } from '@/lib/cn'
import { Button, Select, Textarea, Input, Spinner, Alert, Table, Modal, Badge, Pagination, FileUpload, Tabs, Drawer, ProgressBar } from '@/ui'
import type { AdminSSEEvent, TestGenerateResult } from '@/types'
import styles from './AITestGen.module.css'

// ── Types ──

interface AdminTestGenHistoryItem {
  id: string
  style: string | null
  status: string
  modelId: string | null
  role: string | null
  generatedUrlCount: number
  createdAt: string
}


interface AIModelInfo {
  id: string
  name: string
  provider: string
  enabled: boolean
  isDefault: boolean
  isRecommended: boolean
  costPerImage: number
  facePreservation: string
  speed: string
}

interface ModelsResponse {
  imageModels: AIModelInfo[]
  textModels: unknown[]
}

interface TestGenHistory extends AdminTestGenHistoryItem {
  cost: number | null
  creditsUsed: number | null
  isAdminTest: boolean
  originalUrl: string | null
  generatedUrlCount: number
  originalUrlCount: number
  customPrompt: string | null
  userEmail: string
  userName: string | null
}

interface TestHistoryResponse {
  activities: TestGenHistory[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
  stats: {
    totalCount: number
    totalCost: number
    avgCost: number
    failRate: number
  }
}

interface PromptBlock {
  id: string
  slug: string
  nameKo: string
  nameEn: string
  promptText: string
  imageUrl: string | null
  isBuiltin: boolean
  isActive: boolean
  sortOrder: number
}

interface BlocksResponse {
  blocks: PromptBlock[]
}

interface TestGenDetail {
  id: string
  style: string | null
  status: string
  modelId: string | null
  role: string | null
  cost: number | null
  creditsUsed: number | null
  isAdminTest: boolean
  originalUrl: string | null
  originalUrls: string[] | null
  generatedUrls: string[] | null
  customPrompt: string | null
  userEmail: string
  userName: string | null
  createdAt: string
}

// ── Constants ──

const PROMPT_MODE_TABS: any[] = [
  { key: 'blocks', label: '블록 조합' },
  { key: 'custom', label: '커스텀 프롬프트' },
]

const ROLE_OPTIONS = [
  { value: 'GROOM', label: '신랑' },
  { value: 'BRIDE', label: '신부' },
  { value: 'COUPLE', label: '커플' },
]

const COUNT_OPTIONS = [
  { value: '1', label: '1장' },
  { value: '5', label: '5장' },
  { value: '10', label: '10장' },
]

const HIST_PAGE_SIZE = 10

const STATUS_BADGE: Record<string, { color: 'green' | 'red' | 'yellow' | 'blue' | 'gray'; label: string }> = {
  COMPLETED: { color: 'green', label: '완료' },
  FAILED: { color: 'red', label: '실패' },
  PROCESSING: { color: 'yellow', label: '처리중' },
  PENDING: { color: 'blue', label: '대기' },
}

const ROLE_LABEL: Record<string, string> = {
  GROOM: '신랑',
  BRIDE: '신부',
  COUPLE: '커플',
}

// ── Component ──

export function AITestGen() {
  const { addToast } = useToast()

  // 설정 상태
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  // 커플 모드: 신랑(index 0), 신부(index 1) 분리 슬롯
  const [coupleFiles, setCoupleFiles] = useState<[File | null, File | null]>([null, null])
  const [couplePreviews, setCouplePreviews] = useState<[string | null, string | null]>([null, null])
  const [modelId, setModelId] = useState('')
  const [role, setRole] = useState('COUPLE')
  const [count, setCount] = useState(1)
  const [customPrompt, setCustomPrompt] = useState('')

  // 프롬프트 모드: blocks vs custom
  const [promptMode, setPromptMode] = useState('custom')
  const [conceptSlug, setConceptSlug] = useState('')
  const [previewPrompt, setPreviewPrompt] = useState('')
  const [previewLoading, setPreviewLoading] = useState(false)

  // SSE 스트리밍 생성
  const sse = useSSEGenerate<AdminSSEEvent>()
  const [result, setResult] = useState<TestGenerateResult | null>(null)

  // SSE 이벤트에서 상태 파생
  const generating = sse.phase === 'generating'
  const genError = sse.error

  const statusMessage = useMemo(() => {
    const last = [...sse.events].reverse().find((e): e is Extract<AdminSSEEvent, { type: 'status' }> => e.type === 'status')
    return last?.message ?? null
  }, [sse.events])

  const streamedImages = useMemo(() => {
    return sse.events
      .filter((e): e is Extract<AdminSSEEvent, { type: 'image' }> => e.type === 'image')
      .map((e) => e.url)
  }, [sse.events])

  const batchProgress = useMemo(() => {
    const successEvents = sse.events.filter((e) => e.type === 'image')
    const failEvents = sse.events.filter((e) => e.type === 'image_error')
    const allEvents = [...successEvents, ...failEvents]
    if (allEvents.length === 0) return null
    const total = (allEvents[0] as any).total as number
    return { completed: allEvents.length, total, failed: failEvents.length }
  }, [sse.events])

  // done 이벤트 → result 세팅 + 이력 새로고침
  const prevDoneRef = useRef<string | null>(null)

  // 프롬프트 블록 저장 모달
  const [showBlockSaveModal, setShowBlockSaveModal] = useState(false)
  const [blockSlug, setBlockSlug] = useState('')
  const [blockNameKo, setBlockNameKo] = useState('')
  const [blockNameEn, setBlockNameEn] = useState('')
  const [blockImageUrl, setBlockImageUrl] = useState<string | null>(null)

  // 이력 상태
  const [histPage, setHistPage] = useState(1)
  const [histModelFilter, setHistModelFilter] = useState('')
  const [selectedHistId, setSelectedHistId] = useState<string | null>(null)
  const { data: detailData, error: detailError } = useApiFetch<TestGenDetail>(
    selectedHistId ? `/api/admin/ai-activity-history/${selectedHistId}` : null
  )
  const lastDetailRef = useRef<TestGenDetail | null>(null)
  if (detailData) lastDetailRef.current = detailData
  const displayedDetail = detailData ?? lastDetailRef.current

  // 이미지 프리뷰 모달
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)

  // 모델 목록
  const { data: modelsData } = useApiFetch<ModelsResponse>('/api/admin/ai-models')

  const modelOptions = useMemo(() => {
    const placeholder = { value: '', label: '선택하기' }
    if (!modelsData?.imageModels?.length) return [placeholder]
    const items = modelsData.imageModels
      .filter((m) => m.enabled)
      .map((m) => ({
        value: m.id,
        label: `${m.name}${m.isDefault ? ' (기본)' : ''}${m.isRecommended ? ' ★' : ''}`,
      }))
    return [placeholder, ...items]
  }, [modelsData])

  const estimatedCost = useMemo(() => {
    if (!modelsData?.imageModels?.length || !modelId) return null
    const model = modelsData.imageModels.find((m) => m.id === modelId)
    if (!model) return null
    return model.costPerImage * count
  }, [modelsData, modelId, count])

  // 모델은 사용자가 직접 선택해야 함 (자동 선택 없음)

  // 프롬프트 블록 목록
  const { data: blocksData } = useApiFetch<BlocksResponse>('/api/admin/prompt-blocks')

  const conceptOptions = useMemo(() => {
    if (!blocksData?.blocks) return []
    return blocksData.blocks.map((b) => ({ value: b.slug, label: b.nameKo }))
  }, [blocksData])

  // 블록 데이터 로드 시 기본값 설정
  useEffect(() => {
    if (!blocksData?.blocks?.length) return
    if (!conceptSlug) {
      const first = blocksData.blocks[0]
      if (first) setConceptSlug(first.slug)
    }
  }, [blocksData, conceptSlug])

  // 프롬프트 미리보기
  const handlePreview = useCallback(async () => {
    if (!conceptSlug) return
    setPreviewLoading(true)
    try {
      const res = await fetch('/api/admin/prompt-blocks/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          conceptSlug,
        }),
      })
      const json = await res.json()
      if (json.success) {
        setPreviewPrompt(json.data.promptText ?? json.data.prompt ?? '')
      }
    } catch {
      // ignore
    } finally {
      setPreviewLoading(false)
    }
  }, [conceptSlug])

  // 이력 목록
  const historyUrl = `/api/admin/ai-activity-history?isAdminTest=true&page=${histPage}&pageSize=${HIST_PAGE_SIZE}&sortBy=createdAt&sortOrder=desc${histModelFilter ? `&modelId=${histModelFilter}` : ''}`
  const { data: historyData, refetch: refetchHistory } = useApiFetch<TestHistoryResponse>(historyUrl)

  const { trigger: saveBlock, isLoading: savingBlock } = useApiMutation<unknown, Record<string, unknown>>(
    '/api/admin/prompt-blocks',
    'POST',
  )

  // 이미지 선택 핸들러 (단일 — GROOM/BRIDE)
  const handleImageChange = useCallback((files: File[], previews: string[]) => {
    setImageFiles(files)
    setImagePreviews(previews)
  }, [])

  // 커플 이미지 선택 핸들러 (신랑=0, 신부=1)
  const handleCoupleGroomChange = useCallback((files: File[], previews: string[]) => {
    setCoupleFiles((prev) => [files[0] ?? null, prev[1]])
    setCouplePreviews((prev) => [previews[0] ?? null, prev[1]])
  }, [])

  const handleCoupleBrideChange = useCallback((files: File[], previews: string[]) => {
    setCoupleFiles((prev) => [prev[0], files[0] ?? null])
    setCouplePreviews((prev) => [prev[0], previews[0] ?? null])
  }, [])

  // 모델 필터 변경 시 페이지 리셋
  useEffect(() => {
    setHistPage(1)
  }, [histModelFilter])

  // 유효한 파일/프리뷰 (역할에 따라 분기)
  const effectiveFiles = role === 'COUPLE'
    ? coupleFiles.filter((f): f is File => f !== null)
    : imageFiles
  const effectivePreviews = role === 'COUPLE'
    ? couplePreviews.filter((p): p is string => p !== null)
    : imagePreviews

  // done 이벤트 감지 → result 세팅 + 이력 새로고침
  useEffect(() => {
    const doneEvent = sse.events.find(
      (e): e is Extract<AdminSSEEvent, { type: 'done' }> => e.type === 'done',
    )
    if (!doneEvent) return
    // 같은 done 이벤트 중복 처리 방지
    if (prevDoneRef.current === doneEvent.generationId) return
    prevDoneRef.current = doneEvent.generationId

    setResult({
      generationId: doneEvent.generationId,
      urls: doneEvent.urls,
      cost: doneEvent.cost,
      modelId: doneEvent.modelId,
      prompt: doneEvent.prompt,
      failureCount: doneEvent.failureCount,
    })
    if (doneEvent.failureCount && doneEvent.failureCount > 0) {
      addToast({ type: 'info', message: `${doneEvent.urls.length}장 성공, ${doneEvent.failureCount}장 실패` })
    } else {
      addToast({ type: 'success', message: '이미지 생성 완료!' })
    }
    refetchHistory()
  }, [sse.events, addToast, refetchHistory])

  // 생성 요청 (SSE 스트리밍)
  const handleGenerate = useCallback(async () => {
    const files = role === 'COUPLE'
      ? coupleFiles.filter((f): f is File => f !== null)
      : imageFiles

    if (files.length === 0) {
      addToast({ type: 'error', message: '참조 이미지를 선택해주세요.' })
      return
    }
    if (role === 'COUPLE' && files.length < 2) {
      addToast({ type: 'error', message: '커플 모드는 신랑/신부 이미지를 모두 선택해주세요.' })
      return
    }

    // 모든 이미지를 base64 데이터 URL로 변환
    const imageUrls = await Promise.all(
      files.map(
        (file) =>
          new Promise<string>((resolve) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.readAsDataURL(file)
          })
      )
    )

    const body: Record<string, unknown> = {
      imageUrls,
      modelId,
      role,
      count,
    }

    if (promptMode === 'blocks' && conceptSlug) {
      body.promptBlocks = { conceptSlug }
    } else if (customPrompt.trim()) {
      body.customPrompt = customPrompt
    } else {
      addToast({ type: 'error', message: '블록을 선택하거나 커스텀 프롬프트를 입력해주세요.' })
      return
    }

    setResult(null)
    prevDoneRef.current = null

    sse.start({
      url: '/api/admin/ai-test-generate',
      buildBody: () => JSON.stringify(body),
    })
  }, [imageFiles, coupleFiles, modelId, role, count, promptMode, conceptSlug, customPrompt, addToast, sse])

  // 프롬프트 블록으로 저장 모달 열기
  const handleOpenBlockSave = useCallback(() => {
    const prompt = customPrompt || result?.prompt || ''
    if (!prompt.trim()) {
      addToast({ type: 'error', message: '저장할 프롬프트가 없습니다.' })
      return
    }
    setBlockSlug('')
    setBlockNameKo('')
    setBlockNameEn('')
    setBlockImageUrl(result?.urls[0] ?? null)
    setShowBlockSaveModal(true)
  }, [customPrompt, result, addToast])

  // 프롬프트 블록 저장
  const handleSaveBlock = useCallback(async () => {
    if (!blockSlug.trim() || !blockNameKo.trim() || !blockNameEn.trim()) return
    const promptText = customPrompt || result?.prompt || ''
    if (!promptText.trim()) return

    const res = await saveBlock({
      slug: blockSlug.toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
      nameKo: blockNameKo,
      nameEn: blockNameEn,
      promptText,
      imageUrl: blockImageUrl || undefined,
      isActive: true,
    })

    if (res) {
      addToast({ type: 'success', message: '프롬프트 블록이 저장되었습니다.' })
      setShowBlockSaveModal(false)
    } else {
      addToast({ type: 'error', message: '프롬프트 블록 저장 실패' })
    }
  }, [blockSlug, blockNameKo, blockNameEn, customPrompt, result, blockImageUrl, saveBlock, addToast])

  const historyColumns: any[] = [
    {
      key: 'id',
      label: 'ID',
      render: (row: TestGenHistory) => <span className={styles.idCell}>{row.id.slice(0, 8)}</span>,
      width: '90px',
    },
    {
      key: 'createdAt',
      label: '생성일시',
      render: (row: TestGenHistory) => formatDate(row.createdAt, 'M/d HH:mm'),
      width: '100px',
    },
    {
      key: 'modelId',
      label: '모델',
      render: (row: TestGenHistory) => row.modelId ?? '-',
      width: '140px',
    },
    {
      key: 'style',
      label: '스타일',
      render: (row: TestGenHistory) => row.style ?? '-',
      width: '120px',
    },
    {
      key: 'role',
      label: '역할',
      render: (row: TestGenHistory) => row.role ? (ROLE_LABEL[row.role] ?? row.role) : '-',
      width: '70px',
    },
    {
      key: 'status',
      label: '상태',
      render: (row: TestGenHistory) => {
        const badge = STATUS_BADGE[row.status] ?? { color: 'gray' as const, label: row.status }
        return <Badge color={badge.color}>{badge.label}</Badge>
      },
      width: '80px',
    },
    {
      key: 'cost',
      label: '비용',
      render: (row: TestGenHistory) => row.cost != null ? `$${row.cost.toFixed(4)}` : '-',
      width: '80px',
    },
    {
      key: 'generatedUrlCount',
      label: '이미지',
      render: (row: TestGenHistory) => {
        return row.generatedUrlCount > 0 ? <Badge color="blue">{row.generatedUrlCount}장</Badge> : <span>-</span>
      },
      width: '70px',
    },
  ]

  return (
    <div className={styles.root}>
      {/* 설정 영역 */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>AI 이미지 생성 테스트</h3>
        <div className={styles.configGrid}>
          {role === 'COUPLE' ? (
            <div className={styles.coupleUploadRow}>
              <FileUpload
                label="신랑 이미지"
                accept="image/jpeg,image/png"
                placeholder="신랑 사진을 업로드하세요"
                files={coupleFiles[0] ? [coupleFiles[0]] : []}
                previews={couplePreviews[0] ? [couplePreviews[0]] : []}
                onChange={handleCoupleGroomChange}
              />
              <FileUpload
                label="신부 이미지"
                accept="image/jpeg,image/png"
                placeholder="신부 사진을 업로드하세요"
                files={coupleFiles[1] ? [coupleFiles[1]] : []}
                previews={couplePreviews[1] ? [couplePreviews[1]] : []}
                onChange={handleCoupleBrideChange}
              />
            </div>
          ) : (
            <FileUpload
              label="참조 이미지"
              accept="image/jpeg,image/png"
              multiple
              maxFiles={5}
              files={imageFiles}
              previews={imagePreviews}
              onChange={handleImageChange}
            />
          )}

          <div className={styles.configRow}>
            <div className={styles.configField}>
              <label className={styles.label}>모델</label>
              <Select
                size="sm"
                value={modelId}
                onChange={(e: any) => setModelId(e.target.value)}
                options={modelOptions}
              />
            </div>
            <div className={styles.configField}>
              <label className={styles.label}>역할</label>
              <div className={styles.countToggle}>
                {ROLE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={cn(styles.countBtn, role === opt.value && styles.countBtnActive)}
                    onClick={() => setRole(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.configField}>
              <label className={styles.label}>생성 수량</label>
              <div className={styles.countToggle}>
                {COUNT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={cn(styles.countBtn, count === Number(opt.value) && styles.countBtnActive)}
                    onClick={() => setCount(Number(opt.value))}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <Tabs items={PROMPT_MODE_TABS} value={promptMode} onChange={setPromptMode} />

          {promptMode === 'blocks' ? (
            <>
              <div className={styles.configRow}>
                <div className={styles.configField}>
                  <label className={styles.label}>컨셉</label>
                  <Select size="sm" value={conceptSlug} onChange={(e: any) => setConceptSlug(e.target.value)} options={conceptOptions.length > 0 ? conceptOptions : [{ value: '', label: '로딩 중...' }]} />
                </div>
                <div className={styles.configField} style={{ justifyContent: 'flex-end' }}>
                  <Button size="sm" variant="secondary" onClick={handlePreview} disabled={previewLoading || !conceptSlug}>
                    {previewLoading ? '로딩...' : '프롬프트 미리보기'}
                  </Button>
                </div>
              </div>
              {previewPrompt && (
                <div className={styles.configField}>
                  <label className={styles.label}>조립된 프롬프트</label>
                  <pre className={styles.promptPreviewBlock}>{previewPrompt}</pre>
                </div>
              )}
            </>
          ) : (
            <div className={styles.configField}>
              <label className={styles.label}>커스텀 프롬프트</label>
              <Textarea value={customPrompt} onChange={(e: any) => setCustomPrompt(e.target.value)} placeholder="직접 프롬프트를 입력하세요" rows={4} />
            </div>
          )}

          <div className={styles.generateRow}>
            <Button onClick={handleGenerate} disabled={generating || !modelId || effectiveFiles.length === 0 || (role === 'COUPLE' && effectiveFiles.length < 2)} variant="primary">
              {generating ? <><Spinner size="sm" /> 생성 중...</> : count > 1 ? `${count}장 생성` : '생성'}
            </Button>
            {estimatedCost !== null && count > 1 && (
              <span className={styles.estimatedCost}>예상 비용: ${estimatedCost.toFixed(4)}</span>
            )}
          </div>
        </div>
      </section>

      {genError && <Alert variant="danger">{genError}</Alert>}

      {(generating || (genError && streamedImages.length > 0)) && (
        <section className={styles.progressArea}>
          <div className={styles.statusRow}>
            {generating && <Spinner size="sm" />}
            <span className={styles.statusMessage}>{statusMessage ?? (generating ? '준비 중...' : '')}</span>
          </div>
          {batchProgress && batchProgress.total > 1 && (
            <div className={styles.batchProgressRow}>
              <ProgressBar value={(batchProgress.completed / batchProgress.total) * 100} size="sm" animated={generating} />
              <span className={styles.batchProgressLabel}>
                {batchProgress.completed}/{batchProgress.total} 완료
                {batchProgress.failed > 0 && ` (${batchProgress.failed}장 실패)`}
              </span>
            </div>
          )}
          {streamedImages.length > 0 && (
            <div className={styles.streamedImages}>
              {streamedImages.map((url, i) => (
                <img key={i} src={url} alt={`생성 중 ${i + 1}`} className={styles.streamedThumb} />
              ))}
            </div>
          )}
        </section>
      )}

      {result && (
        <section className={styles.section}>
          <div className={styles.resultHeader}>
            <h3 className={styles.sectionTitle}>결과</h3>
            {result.failureCount != null && result.failureCount > 0 && (
              <Badge color="yellow">{result.urls.length}장 성공 / {result.failureCount}장 실패</Badge>
            )}
          </div>
          <div className={styles.resultViewer}>
            <div className={styles.resultCompare}>
              <div className={styles.compareCol}>
                <label className={styles.label}>원본 이미지 ({effectivePreviews.length}장)</label>
                {effectivePreviews.length > 0 ? (
                  <div className={styles.compareGrid}>
                    {effectivePreviews.map((src, i) => (<img key={i} src={src} alt={`원본 ${i + 1}`} className={styles.compareThumb} />))}
                  </div>
                ) : (
                  <div className={cn(styles.compareImage, styles.comparePlaceholder)}>원본 없음</div>
                )}
              </div>
              <div className={styles.compareCol}>
                <label className={styles.label}>생성 이미지 ({result.urls.length}장)</label>
                <div className={styles.compareGrid}>
                  {result.urls.map((url, i) => (<img key={i} src={url} alt={`생성 결과 ${i + 1}`} className={styles.compareThumb} onClick={() => setPreviewImageUrl(url)} />))}
                </div>
              </div>
            </div>
            <div className={styles.resultMetaBar}>
              <Badge color="blue">{result.modelId}</Badge>
              <span className={styles.resultMetaText}>비용: ${result.cost.toFixed(4)}</span>
              {result.prompt && (<span className={styles.resultMetaText}>프롬프트: {result.prompt.slice(0, 60)}{result.prompt.length > 60 ? '...' : ''}</span>)}
            </div>
            <div className={styles.resultActions}>
              <Button size="sm" variant="primary" onClick={handleOpenBlockSave}>프롬프트 블록으로 저장</Button>
            </div>
          </div>
        </section>
      )}

      <section className={styles.section}>
        <div className={styles.histHeader}>
          <h3 className={styles.sectionTitle}>테스트 생성 이력</h3>
          <div className={styles.histFilters}>
            <Select size="sm" value={histModelFilter} onChange={(e: any) => setHistModelFilter(e.target.value)} options={[{ value: '', label: '전체 모델' }, ...modelOptions]} />
          </div>
        </div>
        {historyData?.activities?.length ? (
          <>
            <Table columns={historyColumns} data={historyData.activities} rowKey={(row: TestGenHistory) => row.id} onRowClick={(row: TestGenHistory) => setSelectedHistId(row.id)} />
            {historyData.pagination.totalPages > 1 && (<Pagination current={histPage} total={historyData.pagination.totalPages} onChange={setHistPage} size="sm" />)}
          </>
        ) : (
          <p className={styles.emptyText}>테스트 생성 이력이 없습니다.</p>
        )}
      </section>


      <Drawer open={!!selectedHistId} onClose={() => setSelectedHistId(null)} title="생성 결과 상세" size="lg">
        {displayedDetail ? (
          <div className={styles.detailContent}>
            <div className={styles.detailCompare}>
              <div className={styles.detailCol}>
                {(() => {
                  const urls = displayedDetail.originalUrls ?? (displayedDetail.originalUrl ? [displayedDetail.originalUrl] : [])
                  return (
                    <>
                      <label className={styles.label}>원본 이미지 ({urls.length}장)</label>
                      {urls.length > 0 ? (
                        <div className={styles.compareGrid}>
                          {urls.map((url, i) => (<img key={i} src={url} alt={`원본 ${i + 1}`} className={styles.detailThumb} onClick={() => setPreviewImageUrl(url)} />))}
                        </div>
                      ) : (
                        <div className={cn(styles.detailOriginal, styles.comparePlaceholder)}>원본 없음</div>
                      )}
                    </>
                  )
                })()}
              </div>
              <div className={styles.detailCol}>
                <label className={styles.label}>생성 이미지 ({displayedDetail.generatedUrls?.length ?? 0}장)</label>
                <div className={styles.compareGrid}>
                  {displayedDetail.generatedUrls?.map((url, i) => (<img key={i} src={url} alt={`생성 ${i + 1}`} className={styles.detailThumb} onClick={() => setPreviewImageUrl(url)} />))}
                  {(!displayedDetail.generatedUrls || displayedDetail.generatedUrls.length === 0) && (<span className={styles.emptyText}>생성된 이미지 없음</span>)}
                </div>
              </div>
            </div>
            <div className={styles.detailMeta}>
              <div className={styles.detailMetaRow}><span className={styles.detailMetaLabel}>모델</span><Badge color="blue">{displayedDetail.modelId ?? '-'}</Badge></div>
              <div className={styles.detailMetaRow}><span className={styles.detailMetaLabel}>스타일</span><span>{displayedDetail.style ?? '-'}</span></div>
              <div className={styles.detailMetaRow}><span className={styles.detailMetaLabel}>역할</span><span>{displayedDetail.role ? (ROLE_LABEL[displayedDetail.role] ?? displayedDetail.role) : '-'}</span></div>
              <div className={styles.detailMetaRow}><span className={styles.detailMetaLabel}>비용</span><span>{displayedDetail.cost != null ? `$${displayedDetail.cost.toFixed(4)}` : '-'}</span></div>
              <div className={styles.detailMetaRow}><span className={styles.detailMetaLabel}>상태</span>{(() => { const badge = STATUS_BADGE[displayedDetail.status] ?? { color: 'gray' as const, label: displayedDetail.status }; return <Badge color={badge.color}>{badge.label}</Badge> })()}</div>
              <div className={styles.detailMetaRow}><span className={styles.detailMetaLabel}>생성일시</span><span>{formatDate(displayedDetail.createdAt, 'yyyy년 M월 d일 HH:mm:ss')}</span></div>
            </div>
            {displayedDetail.customPrompt && (
              <div className={styles.detailPromptSection}>
                <label className={styles.label}>프롬프트</label>
                <p className={styles.detailPrompt}>{displayedDetail.customPrompt}</p>
              </div>
            )}
          </div>
        ) : selectedHistId ? (
          detailError
            ? <Alert variant="danger">{detailError}</Alert>
            : <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner /></div>
        ) : null}
      </Drawer>

      <Modal open={!!previewImageUrl} onClose={() => setPreviewImageUrl(null)} title="이미지 프리뷰" size="lg">
        {previewImageUrl && (
          <div className={styles.imagePreviewModal}>
            <img src={previewImageUrl} alt="프리뷰" className={styles.imagePreviewFull} />
            <a href={previewImageUrl} target="_blank" rel="noopener noreferrer" className={styles.imagePreviewLink}>원본 크기로 보기</a>
          </div>
        )}
      </Modal>

      <Modal open={showBlockSaveModal} onClose={() => setShowBlockSaveModal(false)} title="프롬프트 블록으로 저장">
        <div className={styles.modalContent}>
          <Input label="Slug (영문 대문자 + 언더스코어)" value={blockSlug} onChange={(e: any) => setBlockSlug(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))} placeholder="예: SUNSET_ROMANTIC" />
          <Input label="한국어 이름" value={blockNameKo} onChange={(e: any) => setBlockNameKo(e.target.value)} placeholder="예: 선셋 로맨틱" />
          <Input label="영어 이름" value={blockNameEn} onChange={(e: any) => setBlockNameEn(e.target.value)} placeholder="예: Sunset Romantic" />
          <div className={styles.modalPromptPreview}>
            <label className={styles.label}>프롬프트</label>
            <pre className={styles.promptPreviewBlock}>{(customPrompt || result?.prompt || '').slice(0, 300)}{(customPrompt || result?.prompt || '').length > 300 ? '...' : ''}</pre>
          </div>
          {result && result.urls.length > 0 && (
            <div>
              <label className={styles.label}>대표 이미지 선택</label>
              <div className={styles.compareGrid}>
                {result.urls.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt={`결과 ${i + 1}`}
                    className={styles.compareThumb}
                    style={{ outline: blockImageUrl === url ? '3px solid var(--color-primary, #3b82f6)' : 'none', cursor: 'pointer' }}
                    onClick={() => setBlockImageUrl(blockImageUrl === url ? null : url)}
                  />
                ))}
              </div>
            </div>
          )}
          <div className={styles.modalActions}>
            <Button variant="ghost" onClick={() => setShowBlockSaveModal(false)}>취소</Button>
            <Button variant="primary" onClick={handleSaveBlock} disabled={savingBlock || !blockSlug.trim() || !blockNameKo.trim() || !blockNameEn.trim()}>저장</Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
